import { Link } from "react-router-dom";
import { JOURNAL, JOURNAL_CATEGORIES } from "@/lib/journal";
import { PageHero, SectionHeading, CTABand } from "@/components/PageBlocks";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { IMAGES } from "@/lib/images";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// Reusable content card — supports articles, guides, stories, and podcasts.
export function JournalCard({ item }) {
  const label = item.type === "podcast" ? "Listen Now" : item.type === "guide" ? "Read Guide" : item.type === "story" ? "Read Story" : "Explore";
  return (
    <Link to={`/journal/${item.slug}`} data-testid={`journal-card-${item.slug}`} className="group block border border-white/10 overflow-hidden bg-noir">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={item.cover} alt={item.title} loading="lazy" className="w-full h-full object-cover brightness-90 group-hover:brightness-100 group-hover:scale-[1.04] transition-all duration-[900ms]" />
      </div>
      <div className="p-7">
        <p className="font-body text-[10px] uppercase tracking-[0.3em] text-gold mb-3">{item.category}{item.readTime ? ` · ${item.readTime}` : ""}</p>
        <h3 className="font-serif text-2xl font-light leading-tight group-hover:text-gold transition-colors">{item.title}</h3>
        <p className="font-body text-sm text-white/55 mt-3 font-light leading-relaxed line-clamp-2">{item.excerpt}</p>
        <span className="mt-5 inline-block text-gold text-[11px] uppercase tracking-[0.2em] link-underline">{label}</span>
      </div>
    </Link>
  );
}

export default function Journal() {
  useSeo({ title: "The Passport Journal | Inforno Travel", description: "Stories, ideas, and inspiration for wherever your passport takes you next — by Inforno Travel.", noindex: true });
  const featured = JOURNAL.find((j) => j.featured) || JOURNAL[0];
  const rest = JOURNAL.filter((j) => j !== featured);

  return (
    <div className="bg-noir min-h-screen">
      <Navbar />
      <PageHero overline="by Inforno Travel" title="The Passport Journal" image={IMAGES.dest2}
        sub="Stories, ideas, and inspiration for wherever your passport takes you next." />

      {JOURNAL.length === 0 ? (
        <section className="py-24 md:py-32 px-6 md:px-12 bg-noir">
          <div className="max-w-[900px] mx-auto text-center">
            <SectionHeading center overline="New Stories on the Way" title="Fresh inspiration is being written." sub="Our first travel guides, stories, and podcast episodes are on the way. In the meantime, our specialists are ready to help you start planning." />
          </div>
        </section>
      ) : (
        <>
          {featured && (
            <section className="py-20 md:py-28 px-6 md:px-12 bg-noir">
              <div className="max-w-[1500px] mx-auto">
                <p className="font-body text-[11px] uppercase tracking-[0.4em] text-gold mb-8">Featured Story</p>
                <Link to={`/journal/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                  <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                    <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms]" />
                  </div>
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-[0.3em] text-gold mb-4">{featured.category}</p>
                    <h2 className="font-serif text-4xl md:text-5xl font-light leading-[0.98] group-hover:text-gold transition-colors">{featured.title}</h2>
                    <p className="font-body text-white/55 mt-6 font-light leading-relaxed">{featured.excerpt}</p>
                  </div>
                </Link>
              </div>
            </section>
          )}
          <section className="py-16 md:py-24 px-6 md:px-12 bg-[#0a0a0a] border-y border-white/10">
            <div className="max-w-[1500px] mx-auto">
              <div className="mb-10"><SectionHeading overline="Latest" title="Latest from The Passport Journal" /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((item, i) => <Reveal key={item.slug} delay={(i % 3) * 0.08}><JournalCard item={item} /></Reveal>)}
              </div>
            </div>
          </section>
          <section className="py-16 md:py-24 px-6 md:px-12 bg-noir">
            <div className="max-w-[1500px] mx-auto">
              <div className="mb-8"><SectionHeading overline="Explore By Interest" title="Browse by topic" /></div>
              <div className="flex flex-wrap gap-3">
                {JOURNAL_CATEGORIES.map((c) => <span key={c} className="border border-white/15 text-white/70 px-5 py-3 font-body text-sm hover:border-gold hover:text-gold transition-colors">{c}</span>)}
              </div>
            </div>
          </section>
        </>
      )}

      <CTABand eyebrow="Need a little passport inspiration?" title="Where will your passport take you next?" />
      <Footer />
    </div>
  );
}
